import { cloneDeep } from 'lodash-es';

import {
  createUrl,
  createUrlWithParam,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import {
  defaultDaypartingUI,
  IDaypartingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import {
  defaultLocalTargetingUI,
  ILocalTargetingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/common';
import { IItemIDNameRoute } from '~/src/dash/components/tables/IDNameTable/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { fromCountryToFlag, getAllCountries } from '~/src/dash/utils/countries';
import { isPublisherSamsung } from '~/src/dash/utils/publishers';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { TargetingPresetParamKey } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';

export const SlotOptions: ISelectOption[] = [
  // id 0 means all
  { id: 1, label: '1' },
  { id: 2, label: '2' },
  { id: 3, label: '3' },
  { id: 4, label: '4' },
  { id: 5, label: '5' },
  { id: 6, label: '6' },
  { id: 7, label: '7' },
  { id: 8, label: '8' },
  { id: 9, label: '9' },
  { id: 10, label: '10' },
];

export enum EPositionTargeting {
  ALL = 0,
  PREROLL,
  MIDROLL,
  POSTROLL,
}

export const PositionTargetingOptions: ISelectOption[] = [
  { id: EPositionTargeting.ALL, label: 'All' },
  { id: EPositionTargeting.PREROLL, label: 'Preroll' },
  { id: EPositionTargeting.MIDROLL, label: 'Midroll' },
  { id: EPositionTargeting.POSTROLL, label: 'Postroll' },
];

export enum EPodSequenceTargeting {
  ANY = 0,
  FIRST,
  LAST,
  FIRST_AND_LAST,
}

export const PodSequenceTargetingOptions: ISelectOption[] = [
  { id: EPodSequenceTargeting.ANY, label: 'Any Pod' },
  { id: EPodSequenceTargeting.FIRST, label: 'First Pod' },
  { id: EPodSequenceTargeting.LAST, label: 'Last Pod' },
  { id: EPodSequenceTargeting.FIRST_AND_LAST, label: 'First & Last Pods' },
];

export enum ETargetedAdBreak {
  ALL = 0,
  FIRST,
  LAST,
}

export const TargetedAdBreakOptions: ISelectOption[] = [
  { id: ETargetedAdBreak.ALL, label: 'All' },
  { id: ETargetedAdBreak.FIRST, label: 'First' },
  { id: ETargetedAdBreak.LAST, label: 'Last' },
];

export enum EAllowStatus {
  UNKNOWN = 0,
  DENY,
  ALLOW,
}

export enum ELogicalOperator {
  OR = 0,
  AND,
}

export enum ETargetingCOPPA {
  COPPA_ALL = 0,
  COPPA_0,
  COPPA_1,
}

export const TargetingCOPPAOptions: ISelectOption[] = [
  { id: ETargetingCOPPA.COPPA_ALL, label: 'All' },
  { id: ETargetingCOPPA.COPPA_0, label: '0' },
  { id: ETargetingCOPPA.COPPA_1, label: '1' },
];

export enum ETargetBool {
  ALL = 0,
  FALSE,
  TRUE,
}

export const TargetBoolOptions: ISelectOption[] = [
  { id: ETargetBool.ALL, label: 'All' },
  { id: ETargetBool.FALSE, label: 'False' },
  { id: ETargetBool.TRUE, label: 'True' },
];

export enum ETargetingCCPA {
  ALL = 0,
  OPT_OUT,
  NON_OPT_OUT,
}

export const TargetingCCPAOptions: ISelectOption[] = [
  { id: ETargetingCCPA.ALL, label: 'All' },
  { id: ETargetingCCPA.OPT_OUT, label: 'Opt Out' },
  { id: ETargetingCCPA.NON_OPT_OUT, label: 'Non Opt Out' },
];

export enum ETargetingGDPR {
  ALL = 0,
  GDPR0,
  GDPR1,
}

export const TargetingGDPROptions: ISelectOption[] = [
  { id: ETargetingGDPR.ALL, label: 'All' },
  { id: ETargetingGDPR.GDPR0, label: '0' },
  { id: ETargetingGDPR.GDPR1, label: '1' },
];

export enum ETargetingGDPRConsent {
  ALL = 0,
  TRUE,
  FALSE,
}

export const TargetingGDPRConsentOptions: ISelectOption[] = [
  { id: ETargetingGDPRConsent.ALL, label: 'All' },
  { id: ETargetingGDPRConsent.TRUE, label: '0' },
  { id: ETargetingGDPRConsent.FALSE, label: '1' },
];

export enum ETargetingLiveStream {
  ALL = 0,
  LIVE,
  VOD,
  DVR,
}

export const TargetingLiveStreamOptions: ISelectOption[] = [
  { id: ETargetingLiveStream.LIVE, label: 'Live' },
  { id: ETargetingLiveStream.VOD, label: 'VOD' },
  { id: ETargetingLiveStream.DVR, label: 'DVR' },
];

export const generateCountryOptions = (): ISelectOption[] => {
  const options: ISelectOption[] = [];
  getAllCountries().forEach((country) => {
    options.push({
      id: country.key,
      label: `${fromCountryToFlag(country)} ${country.text}`,
    });
  });
  return options;
};

// TODO: ARRAYS LIKE THIS SHOULD BE MOVED TO THE BACKEND
export const DeviceOptions: ISelectOption[] = [
  // id 0 means all
  { id: 1, label: 'Android Mobile' },
  { id: 2, label: 'Android Tablet' },
  { id: 3, label: 'Android TV' },
  { id: 4, label: 'Fire TV' },
  { id: 5, label: 'iOS Mobile' },
  { id: 6, label: 'iOS Tablet' },
  { id: 7, label: 'Apple TV' },
  { id: 8, label: 'Playstation' },
  { id: 9, label: 'Roku' },
  { id: 10, label: 'Xbox' },
  { id: 11, label: 'Samsung TV' },
  { id: 12, label: 'Chromecast' },
  { id: 13, label: 'Other' },
  { id: 14, label: 'Vizio' },
  { id: 15, label: 'LG' },
  { id: 16, label: 'Dish' },
  { id: 17, label: 'Hisense' },
  { id: 18, label: 'Nvidia' },
  { id: 19, label: 'Phillips' },
  { id: 20, label: 'Huawei' },
  { id: 21, label: 'Tcl' },
  { id: 22, label: 'Tivo' },
  { id: 23, label: 'Bravia' },
  { id: 24, label: 'Comcast' },
  { id: 25, label: 'Sharp' },
  { id: 26, label: 'Panasonic' },
  { id: 27, label: 'Sanyo' },
  { id: 28, label: 'Desktop' },
  { id: 29, label: 'AirTV' },
  { id: 30, label: 'Xiaomi' },
  { id: 31, label: 'AT&T' },
  { id: 32, label: 'Samsung Family Hub' },
];

export const DeviceORTBTypeOptions: ISelectOption[] = [
  { id: 0, label: 'Unknown Device Type' },
  { id: 2, label: 'Desktop' },
  { id: 3, label: 'Connected TV' },
  { id: 4, label: 'Mobile' },
  { id: 5, label: 'Tablet' },
  { id: 6, label: 'Connected Device' },
  { id: 7, label: 'Set-Top Box' },
];

export const SamsungDeviceSubTypeOptions: ISelectOption[] = [
  // id 0 means all
  { id: 1, label: 'Samsung Tizen 2.3' },
  { id: 2, label: 'Samsung Tizen 2.4' },
  { id: 3, label: 'Samsung Tizen 3.0' },
  { id: 4, label: 'Samsung Tizen 4.0' },
  { id: 5, label: 'Samsung Tizen 5.0' },
  { id: 6, label: 'Samsung Tizen 5.5' },
  { id: 7, label: 'Samsung Tizen 6.0' },
  { id: 8, label: 'Samsung Tizen 6.5' },
  { id: 9, label: 'Samsung Tizen 7.0' },
  { id: 10, label: 'Samsung Tizen 8.0' },
];

// IF YOU ADD TO THIS, MAKE SURE TO ADJUST THE VALUE CONVERSIONS IN TARGETING CONFIG
export const generateDeviceSubTypeOptionsList = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  const options = [] as ISelectOption[];
  if (isPublisherSamsung(session.publisher.ID)) {
    return new Promise((resolve) => resolve(SamsungDeviceSubTypeOptions));
  } else if (hasRoleSuperUser(user.user)) {
    SamsungDeviceSubTypeOptions.forEach((option) => {
      option.label += ' *';
      options.push(option);
    });
  }
  return new Promise((resolve) => resolve(options));
};

export type TargetingPresetKey =
  // CONTENT
  | 'TargetingContentChannel'
  | 'TargetingContentChannelIsAllowed'
  | 'TargetingContentDistributor'
  | 'TargetingContentDistributorIsAllowed'
  | 'TargetingContentGenre'
  | 'TargetingContentGenreIsAllowed'
  | 'TargetingContentID'
  | 'TargetingContentIDIsAllowed'
  | 'TargetingContentNetwork'
  | 'TargetingContentNetworkIsAllowed'
  | 'TargetingContentProducer'
  | 'TargetingContentProducerIsAllowed'
  | 'TargetingContentRating'
  | 'TargetingContentRatingIsAllowed'
  | 'TargetingContentSeries'
  | 'TargetingContentSeriesIsAllowed'
  | 'TargetingContentTitle'
  | 'TargetingContentTitleIsAllowed'
  | 'TargetingContentCategory'
  | 'TargetingContentCategoryIsAllowed'
  // GEO
  | 'TargetingCountryGroupIDs'
  | 'TargetingCountryGroupIDsIsAllowed'
  | 'TargetingDeviceCountry'
  | 'TargetingDeviceCountryIsAllowed'
  | 'LocalTargetingAllowed'
  | 'LocalTargetingAllowedLogic'
  | 'LocalTargetingDisallowed'
  | 'LocalTargetingDisallowedLogic'
  // DAYPARTING
  | 'Dayparting'
  | 'DaypartingIsAllowed'
  // PRIVACY
  | 'TargetingCoppa'
  | 'TargetingLimitedAdTracking'
  | 'TargetingCCPA'
  | 'TargetingGDPR'
  | 'TargetingGDPRConsent'
  // CUSTOM
  | 'Custom4'
  | 'Custom4Inclusion'
  | 'Custom5'
  | 'Custom5Inclusion'
  | 'Custom6'
  | 'Custom6Inclusion'
  | 'Custom7'
  | 'Custom7Inclusion'
  | 'Custom8'
  | 'Custom8Inclusion'
  | 'Custom9'
  | 'Custom9Inclusion'
  | 'Custom10'
  | 'Custom10Inclusion'
  | 'Custom11'
  | 'Custom11Inclusion'
  | 'Custom12'
  | 'Custom12Inclusion'
  | 'Custom13'
  | 'Custom13Inclusion'
  | 'Custom14'
  | 'Custom14Inclusion'
  | 'Custom15'
  | 'Custom15Inclusion'
  | 'Custom16'
  | 'Custom16Inclusion'
  | 'Custom17'
  | 'Custom17Inclusion'
  | 'Custom18'
  | 'Custom18Inclusion'
  | 'CustomTargeting'
  | 'CustomTargetingInclusion'
  // DEVICE
  | 'TargetingDevices'
  | 'TargetingDevicesIsAllowed'
  | 'TargetingDeviceTypes'
  | 'TargetingDeviceTypesIsAllowed'
  | 'TargetingDeviceSubTypes'
  | 'TargetingDeviceSubTypesIsAllowed'
  | 'IPFilter'
  // APP
  | 'TargetingAppBundleID'
  | 'TargetingAppBundleIDIsAllowed'
  // STREAM
  | 'TargetingLivestream'
  // SEGMENTS
  | 'TargetingAudienceSegmentIDs'
  | 'TargetingAudienceSegmentIDsIsAllowed'
  | 'SegmentTargeting'
  | 'TargetingContextualSegmentIDs'
  // SITE PAGES
  | 'TargetingSitePages'
  | 'TargetingSitePagesIsAllowed'
  // SITE DOMAINS
  | 'TargetingSiteDomain'
  | 'TargetingSiteDomainIsAllowed'
  // FOR UI USE ONLY
  | 'DaypartingUI'
  | 'LocalTargetingAllowedUI'
  | 'LocalTargetingDisallowedUI';

export type TargetingPresetListViewAPIKey =
  | 'ID'
  | 'Name'
  | 'UpdatedAt'
  | 'Bidders'
  | 'NumBidders'
  | 'Campaigns'
  | 'NumCampaigns';

export interface TargetingPresetListInfo {
  ID: string;
  Name: string;
  UpdatedAt: string;
  Bidders: IItemIDNameRoute[];
  NumBidders: number;
}

// IF YOU UPDATE THE INTERFACE BELOW, MAKE SURE TO UPDATE THE DEFAULT OBJECT ALSO
export interface ITargetingPreset extends ITargetingPresetVars, ITargetingPresetUIVars {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  PublisherID: number;
  Name: string;
  ActiveParameters: string[] | Record<string, string[]>;
  Bidders: IItemIDNameRoute[];
  TargetingBidderIDs: number[];
  Campaigns: IItemIDNameRoute[];
  TargetingCampaignIDs: number[];
}

export interface ITargetingPresetUIVars {
  // FOR UI USE ONLY!!!
  DaypartingUI: IDaypartingUI;
  LocalTargetingAllowedUI: ILocalTargetingUI[];
  LocalTargetingDisallowedUI: ILocalTargetingUI[];
}

export interface ITargetingPresetVars {
  // CONTENT
  TargetingContentChannel: string;
  TargetingContentChannelIsAllowed: EAllowStatus;
  TargetingContentDistributor: string;
  TargetingContentDistributorIsAllowed: EAllowStatus;
  TargetingContentGenre: string;
  TargetingContentGenreIsAllowed: EAllowStatus;
  TargetingContentID: string;
  TargetingContentIDIsAllowed: EAllowStatus;
  TargetingContentNetwork: string;
  TargetingContentNetworkIsAllowed: EAllowStatus;
  TargetingContentProducer: string;
  TargetingContentProducerIsAllowed: EAllowStatus;
  TargetingContentRating: string;
  TargetingContentRatingIsAllowed: EAllowStatus;
  TargetingContentSeries: string;
  TargetingContentSeriesIsAllowed: EAllowStatus;
  TargetingContentTitle: string;
  TargetingContentTitleIsAllowed: EAllowStatus;
  TargetingContentCategory: string;
  TargetingContentCategoryIsAllowed: EAllowStatus;
  // GEO
  TargetingCountryGroupIDs: number[];
  TargetingCountryGroupIDsIsAllowed: EAllowStatus;
  TargetingDeviceCountry: string;
  TargetingDeviceCountryIsAllowed: EAllowStatus;
  LocalTargetingAllowed: string;
  LocalTargetingAllowedLogic: ELogicalOperator;
  LocalTargetingDisallowed: string;
  LocalTargetingDisallowedLogic: ELogicalOperator;
  // DAYPARTING
  Dayparting: string;
  DaypartingIsAllowed: EAllowStatus;
  // PRIVACY
  TargetingCoppa: ETargetingCOPPA;
  TargetingLimitedAdTracking: ETargetBool;
  TargetingCCPA: ETargetingCCPA;
  TargetingGDPR: ETargetingGDPR;
  TargetingGDPRConsent: ETargetingGDPRConsent;
  // CUSTOM
  Custom4: string;
  Custom4Inclusion: EAllowStatus;
  Custom5: string;
  Custom5Inclusion: EAllowStatus;
  Custom6: string;
  Custom6Inclusion: EAllowStatus;
  Custom7: string;
  Custom7Inclusion: EAllowStatus;
  Custom8: string;
  Custom8Inclusion: EAllowStatus;
  Custom9: string;
  Custom9Inclusion: EAllowStatus;
  Custom10: string;
  Custom10Inclusion: EAllowStatus;
  Custom11: string;
  Custom11Inclusion: EAllowStatus;
  Custom12: string;
  Custom12Inclusion: EAllowStatus;
  Custom13: string;
  Custom13Inclusion: EAllowStatus;
  Custom14: string;
  Custom14Inclusion: EAllowStatus;
  Custom15: string;
  Custom15Inclusion: EAllowStatus;
  Custom16: string;
  Custom16Inclusion: EAllowStatus;
  Custom17: string;
  Custom17Inclusion: EAllowStatus;
  Custom18: string;
  Custom18Inclusion: EAllowStatus;
  CustomTargeting: string;
  CustomTargetingInclusion: EAllowStatus;
  // DEVICE
  TargetingDevices: string;
  TargetingDevicesIsAllowed: EAllowStatus;
  TargetingDeviceTypes: string;
  TargetingDeviceTypesIsAllowed: EAllowStatus;
  TargetingDeviceSubTypes: string;
  TargetingDeviceSubTypesIsAllowed: EAllowStatus;
  IPFilter: string;
  // APP
  TargetingAppBundleID: string;
  TargetingAppBundleIDIsAllowed: boolean;
  // DEALS // CURRENTLY NOT IN USE FOR PRESETS
  TargetingDealID: string;
  TargetingDealIDIsAllowed: EAllowStatus;
  TargetingFWSectionIDs: string;
  TargetingFWSectionIDsIsAllowed: EAllowStatus;
  // STREAM
  TargetingLivestream: string;
  // SEGMENTS
  TargetingAudienceSegmentIDs: number[];
  TargetingAudienceSegmentIDsIsAllowed: EAllowStatus;
  SegmentTargeting: ELogicalOperator;
  TargetingContextualSegmentIDs: number[];
  // SITE PAGES
  TargetingSitePages: string;
  TargetingSitePagesIsAllowed: EAllowStatus;
  // SITE DOMAINS
  TargetingSiteDomain: string;
  TargetingSiteDomainIsAllowed: EAllowStatus;
}

export const TargetingPresetKeys: Record<string, TargetingPresetKey> = {
  // CONTENT
  TARGETING_CONTENT_CHANNEL: 'TargetingContentChannel',
  TARGETING_CONTENT_CHANNEL_IS_ALLOWED: 'TargetingContentChannelIsAllowed',
  TARGETING_CONTENT_DISTRIBUTOR: 'TargetingContentDistributor',
  TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED: 'TargetingContentDistributorIsAllowed',
  TARGETING_CONTENT_GENRE: 'TargetingContentGenre',
  TARGETING_CONTENT_GENRE_IS_ALLOWED: 'TargetingContentGenreIsAllowed',
  TARGETING_CONTENT_ID: 'TargetingContentID',
  TARGETING_CONTENT_ID_IS_ALLOWED: 'TargetingContentIDIsAllowed',
  TARGETING_CONTENT_NETWORK: 'TargetingContentNetwork',
  TARGETING_CONTENT_NETWORK_IS_ALLOWED: 'TargetingContentNetworkIsAllowed',
  TARGETING_CONTENT_PRODUCER: 'TargetingContentProducer',
  TARGETING_CONTENT_PRODUCER_IS_ALLOWED: 'TargetingContentProducerIsAllowed',
  TARGETING_CONTENT_RATING: 'TargetingContentRating',
  TARGETING_CONTENT_RATING_IS_ALLOWED: 'TargetingContentRatingIsAllowed',
  TARGETING_CONTENT_SERIES: 'TargetingContentSeries',
  TARGETING_CONTENT_SERIES_IS_ALLOWED: 'TargetingContentSeriesIsAllowed',
  TARGETING_CONTENT_TITLE: 'TargetingContentTitle',
  TARGETING_CONTENT_TITLE_IS_ALLOWED: 'TargetingContentTitleIsAllowed',
  TARGETING_CONTENT_CATEGORY: 'TargetingContentCategory',
  TARGETING_CONTENT_CATEGORY_IS_ALLOWED: 'TargetingContentCategoryIsAllowed',
  // GEO
  TARGETING_COUNTRY_GROUP_IDS: 'TargetingCountryGroupIDs',
  TARGETING_COUNTRY_GROUP_IDS_IS_ALLOWED: 'TargetingCountryGroupIDsIsAllowed',
  TARGETING_DEVICE_COUNTRY: 'TargetingDeviceCountry',
  TARGETING_DEVICE_COUNTRY_IS_ALLOWED: 'TargetingDeviceCountryIsAllowed',
  LOCAL_TARGETING_ALLOWED: 'LocalTargetingAllowed',
  LOCAL_TARGETING_ALLOWED_LOGIC: 'LocalTargetingAllowedLogic',
  LOCAL_TARGETING_DISALLOWED: 'LocalTargetingDisallowed',
  LOCAL_TARGETING_DISALLOWED_LOGIC: 'LocalTargetingDisallowedLogic',
  // DAYPARTING
  DAYPARTING: 'Dayparting',
  DAYPARTING_IS_ALLOWED: 'DaypartingIsAllowed',
  // PRIVACY
  TARGETING_COPPA: 'TargetingCoppa',
  TARGETING_LIMITED_AD_TRACKING: 'TargetingLimitedAdTracking',
  TARGETING_CCPA: 'TargetingCCPA',
  TARGETING_GDPR: 'TargetingGDPR',
  TARGETING_GDPR_CONSENT: 'TargetingGDPRConsent',
  // CUSTOM
  CUSTOM_4: 'Custom4',
  CUSTOM_4_INCLUSION: 'Custom4Inclusion',
  CUSTOM_5: 'Custom5',
  CUSTOM_5_INCLUSION: 'Custom5Inclusion',
  CUSTOM_6: 'Custom6',
  CUSTOM_6_INCLUSION: 'Custom6Inclusion',
  CUSTOM_7: 'Custom7',
  CUSTOM_7_INCLUSION: 'Custom7Inclusion',
  CUSTOM_8: 'Custom8',
  CUSTOM_8_INCLUSION: 'Custom8Inclusion',
  CUSTOM_9: 'Custom9',
  CUSTOM_9_INCLUSION: 'Custom9Inclusion',
  CUSTOM_10: 'Custom10',
  CUSTOM_10_INCLUSION: 'Custom10Inclusion',
  CUSTOM_11: 'Custom11',
  CUSTOM_11_INCLUSION: 'Custom11Inclusion',
  CUSTOM_12: 'Custom12',
  CUSTOM_12_INCLUSION: 'Custom12Inclusion',
  CUSTOM_13: 'Custom13',
  CUSTOM_13_INCLUSION: 'Custom13Inclusion',
  CUSTOM_14: 'Custom14',
  CUSTOM_14_INCLUSION: 'Custom14Inclusion',
  CUSTOM_15: 'Custom15',
  CUSTOM_15_INCLUSION: 'Custom15Inclusion',
  CUSTOM_16: 'Custom16',
  CUSTOM_16_INCLUSION: 'Custom16Inclusion',
  CUSTOM_17: 'Custom17',
  CUSTOM_17_INCLUSION: 'Custom17Inclusion',
  CUSTOM_18: 'Custom18',
  CUSTOM_18_INCLUSION: 'Custom18Inclusion',
  CUSTOM_TARGETING: 'CustomTargeting',
  CUSTOM_TARGETING_INCLUSION: 'CustomTargetingInclusion',
  // DEVICE
  TARGETING_DEVICES: 'TargetingDevices',
  TARGETING_DEVICES_IS_ALLOWED: 'TargetingDevicesIsAllowed',
  TARGETING_DEVICE_TYPES: 'TargetingDeviceTypes',
  TARGETING_DEVICE_TYPES_IS_ALLOWED: 'TargetingDeviceTypesIsAllowed',
  TARGETING_DEVICE_SUB_TYPES: 'TargetingDeviceSubTypes',
  TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED: 'TargetingDeviceSubTypesIsAllowed',
  IP_FILTER: 'IPFilter',
  // APP
  TARGETING_APP_BUNDLE_ID: 'TargetingAppBundleID',
  TARGETING_APP_BUNDLE_ID_IS_ALLOWED: 'TargetingAppBundleIDIsAllowed',
  // STREAM
  TARGETING_LIVESTREAM: 'TargetingLivestream',
  // SEGMENTS
  TARGETING_AUDIENCE_SEGMENT_IDS: 'TargetingAudienceSegmentIDs',
  TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED: 'TargetingAudienceSegmentIDsIsAllowed',
  SEGMENT_TARGETING: 'SegmentTargeting',
  TARGETING_CONTEXTUAL_SEGMENT_IDS: 'TargetingContextualSegmentIDs',
  // SITE PAGES
  TARGETING_SITE_PAGES: 'TargetingSitePages',
  TARGETING_SITE_PAGES_IS_ALLOWED: 'TargetingSitePagesIsAllowed',
  // SITE DOMAINS
  TARGETING_SITE_DOMAIN: 'TargetingSiteDomain',
  TARGETING_SITE_DOMAIN_IS_ALLOWED: 'TargetingSiteDomainIsAllowed',
  // FOR UI USE ONLY
  DAYPARTING_UI: 'DaypartingUI',
  LOCAL_TARGETING_ALLOWED_UI: 'LocalTargetingAllowedUI',
  LOCAL_TARGETING_DISALLOWED_UI: 'LocalTargetingDisallowedUI',
};

export const defaultTargetingPreset: Partial<ITargetingPreset> = {
  ID: 0,
  PublisherID: 0,
  Name: '',
  ActiveParameters: {},
  Bidders: [],
  TargetingBidderIDs: [],
  Campaigns: [],
  TargetingCampaignIDs: [],
  // CONTENT
  TargetingContentChannel: '',
  TargetingContentChannelIsAllowed: EAllowStatus.ALLOW,
  TargetingContentDistributor: '',
  TargetingContentDistributorIsAllowed: EAllowStatus.ALLOW,
  TargetingContentGenre: '',
  TargetingContentGenreIsAllowed: EAllowStatus.ALLOW,
  TargetingContentID: '',
  TargetingContentIDIsAllowed: EAllowStatus.ALLOW,
  TargetingContentNetwork: '',
  TargetingContentNetworkIsAllowed: EAllowStatus.ALLOW,
  TargetingContentProducer: '',
  TargetingContentProducerIsAllowed: EAllowStatus.ALLOW,
  TargetingContentRating: '',
  TargetingContentRatingIsAllowed: EAllowStatus.ALLOW,
  TargetingContentSeries: '',
  TargetingContentSeriesIsAllowed: EAllowStatus.ALLOW,
  TargetingContentTitle: '',
  TargetingContentTitleIsAllowed: EAllowStatus.ALLOW,
  TargetingContentCategory: '',
  TargetingContentCategoryIsAllowed: EAllowStatus.ALLOW,
  // GEO
  TargetingCountryGroupIDs: [],
  TargetingCountryGroupIDsIsAllowed: EAllowStatus.ALLOW,
  TargetingDeviceCountry: '',
  TargetingDeviceCountryIsAllowed: EAllowStatus.ALLOW,
  LocalTargetingAllowed: '',
  LocalTargetingAllowedLogic: ELogicalOperator.OR,
  LocalTargetingDisallowed: '',
  LocalTargetingDisallowedLogic: ELogicalOperator.OR,
  // DAYPARTING
  Dayparting: '',
  DaypartingIsAllowed: EAllowStatus.ALLOW,
  // PRIVACY
  TargetingCoppa: ETargetingCOPPA.COPPA_ALL,
  TargetingLimitedAdTracking: ETargetBool.ALL,
  TargetingCCPA: ETargetingCCPA.ALL,
  TargetingGDPR: ETargetingGDPR.ALL,
  TargetingGDPRConsent: ETargetingGDPRConsent.ALL,
  // CUSTOM
  Custom4: '',
  Custom4Inclusion: EAllowStatus.ALLOW,
  Custom5: '',
  Custom5Inclusion: EAllowStatus.ALLOW,
  Custom6: '',
  Custom6Inclusion: EAllowStatus.ALLOW,
  Custom7: '',
  Custom7Inclusion: EAllowStatus.ALLOW,
  Custom8: '',
  Custom8Inclusion: EAllowStatus.ALLOW,
  Custom9: '',
  Custom9Inclusion: EAllowStatus.ALLOW,
  Custom10: '',
  Custom10Inclusion: EAllowStatus.ALLOW,
  Custom11: '',
  Custom11Inclusion: EAllowStatus.ALLOW,
  Custom12: '',
  Custom12Inclusion: EAllowStatus.ALLOW,
  Custom13: '',
  Custom13Inclusion: EAllowStatus.ALLOW,
  Custom14: '',
  Custom14Inclusion: EAllowStatus.ALLOW,
  Custom15: '',
  Custom15Inclusion: EAllowStatus.ALLOW,
  Custom16: '',
  Custom16Inclusion: EAllowStatus.ALLOW,
  Custom17: '',
  Custom17Inclusion: EAllowStatus.ALLOW,
  Custom18: '',
  Custom18Inclusion: EAllowStatus.ALLOW,
  CustomTargeting: '',
  CustomTargetingInclusion: EAllowStatus.ALLOW,
  // DEVICE
  TargetingDevices: '',
  TargetingDevicesIsAllowed: EAllowStatus.ALLOW,
  TargetingDeviceTypes: '',
  TargetingDeviceTypesIsAllowed: EAllowStatus.ALLOW,
  TargetingDeviceSubTypes: '0',
  TargetingDeviceSubTypesIsAllowed: EAllowStatus.ALLOW,
  IPFilter: '',
  // APP
  TargetingAppBundleID: '',
  TargetingAppBundleIDIsAllowed: true,
  // DEALS
  TargetingDealID: '',
  TargetingDealIDIsAllowed: EAllowStatus.ALLOW,
  TargetingFWSectionIDs: '',
  TargetingFWSectionIDsIsAllowed: EAllowStatus.ALLOW,
  // STREAM
  TargetingLivestream: '',
  // SEGMENTS
  TargetingAudienceSegmentIDs: [],
  TargetingAudienceSegmentIDsIsAllowed: EAllowStatus.ALLOW,
  SegmentTargeting: ELogicalOperator.OR,
  TargetingContextualSegmentIDs: [],
  // SITE PAGES
  TargetingSitePages: '',
  TargetingSitePagesIsAllowed: EAllowStatus.ALLOW,
  // SITE DOMAINS
  TargetingSiteDomain: '',
  TargetingSiteDomainIsAllowed: EAllowStatus.ALLOW,
  // FOR UI USE ONLY
  DaypartingUI: defaultDaypartingUI,
  LocalTargetingAllowedUI: [cloneDeep(defaultLocalTargetingUI)],
  LocalTargetingDisallowedUI: [cloneDeep(defaultLocalTargetingUI)],
};

interface IDefaultResponse {
  success: boolean;
  msg: string;
}

export interface ITargetingPresetPromise extends IDefaultResponse {
  Preset: ITargetingPreset | null;
}

interface ITargetingPresetIDResponse extends IDefaultResponse {
  ID: number;
}

export async function createTargetingPreset(
  publisherID: string,
  preset: Partial<ITargetingPreset>,
): Promise<ITargetingPresetIDResponse> {
  const reqUrl = `${createUrl('/targeting_preset', publisherID, 'v1')}`;
  const response = await restPost<any>(reqUrl, preset);
  return response.parsedBody;
}

export async function getTargetingPreset(
  publisherID: string,
  presetID: string,
): Promise<ITargetingPresetPromise> {
  const reqUrl = createUrlWithParam('/targeting_preset', publisherID, presetID, 'preset_id');
  const response = await restGet<any>(reqUrl);
  return response.parsedBody;
}

export async function patchTargetingPreset(
  publisherID: string,
  preset: Partial<ITargetingPreset>,
): Promise<ITargetingPresetPromise> {
  const reqUrl = `${createUrl('/targeting_preset', publisherID, 'v1')}`;
  const response = await restPatch<any>(reqUrl, preset);
  return response.parsedBody;
}

export interface IPresetConflict {
  ID: number;
  Name: string;
  Conflicts: TargetingPresetParamKey[];
  ParentID?: number;
}

export interface IPatchTargetingPresetValidation {
  isValid: boolean;
  BidderConflicts: IPresetConflict[];
  CampaignConflicts: IPresetConflict[];
}

export async function validatePatchTargetingPreset(
  publisherID: string,
  preset: Partial<ITargetingPreset>,
): Promise<IPatchTargetingPresetValidation> {
  const reqUrl = `${createUrl('/validate_targeting_preset', publisherID, 'v1')}`;
  const response = await restPost<any>(reqUrl, preset);
  return response.parsedBody;
}

export async function deleteTargetingPreset(
  publisherID: string,
  presetID: string,
): Promise<ITargetingPresetIDResponse> {
  const reqUrl = createUrlWithParam('/targeting_preset', publisherID, presetID, 'preset_id');
  const response = await restDelete<any>(reqUrl, {});
  return response.parsedBody;
}

export interface ISimpleTargetingPreset {
  ID: number;
  Name: string;
  UpdatedAt: string;
  Bidders: IItemIDNameRoute[] | null;
  NumBidders: number;
  Campaigns: IItemIDNameRoute[] | null;
  NumCampaigns: number;
}

export interface ITargetingPresetListResponse extends IDefaultResponse {
  Presets: ISimpleTargetingPreset[] | null;
  NumPresets: number;
}

export async function getTargetingPresetList(
  publisherID: string,
  query: any,
): Promise<ITargetingPresetListResponse> {
  const reqUrl = `${createUrl('/targeting_preset_list', publisherID, 'v1')}`;
  const response = await restPost<any>(reqUrl, query);
  return response.parsedBody;
}

interface ITargetingPresetOptionsPromise {
  Presets: ISelectOption[];
  success: boolean;
}

export async function fetchTargetingPresetOptions(
  publisherID: string,
): Promise<ITargetingPresetOptionsPromise> {
  const reqUrl = createUrl('/targeting_preset_options', publisherID, 'v1');
  const response = await restGet<any>(reqUrl, {});
  return response.parsedBody;
}

// this wrapper allows it to be used in the current format
export const generateTargetingPresetOptionsList = (
  session: ISessionState,
): Promise<ISelectOption[]> => {
  return fetchTargetingPresetOptions(session.publisher.ID)
    .then(({ Presets }) => {
      return Presets || [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

interface ITargetingPresetSummary {
  Targeting: ITargetingPreset[];
  ActiveParameters: TargetingPresetParamKey[];
  ConflictedParameters: TargetingPresetParamKey[];
  ActiveParameterInfo: Record<TargetingPresetParamKey, string[]>;
}

export async function getTargetingPresetSummary(
  publisherID: string,
  query: any,
): Promise<ITargetingPresetSummary> {
  const reqUrl = createUrl('/targeting_preset_summary', publisherID, 'v1');
  const response = await restPost<any>(reqUrl, query);
  return response.parsedBody;
}

export async function validateTargetingPresetSelections(
  publisherID: string,
  query: any,
): Promise<any> {
  const reqUrl = createUrl('/validate_targeting_preset_selections', publisherID, 'v1');
  const response = await restPost<any>(reqUrl, query);
  return response.parsedBody;
}

export async function validateBidderTargetingBulkUpdate(
  publisherID: string,
  query: any,
): Promise<any> {
  const reqUrl = createUrl('/validate_bidder_targeting_bulk_update', publisherID, 'v1');
  const response = await restPost<any>(reqUrl, query);
  return response.parsedBody;
}

export const bidderTargetingFieldKeys = new Set([
  'TargetingContentChannel',
  'TargetingContentChannelIsAllowed',
  'TargetingContentDistributor',
  'TargetingContentDistributorIsAllowed',
  'TargetingContentGenre',
  'TargetingContentGenreIsAllowed',
  'TargetingContentID',
  'TargetingContentIDIsAllowed',
  'TargetingContentNetwork',
  'TargetingContentNetworkIsAllowed',
  'TargetingContentRating',
  'TargetingContentRatingIsAllowed',
  'TargetingContentSeries',
  'TargetingContentSeriesIsAllowed',
  'TargetingContentTitle',
  'TargetingContentTitleIsAllowed',
  'TargetingContentCategory',
  'TargetingContentCategoryIsAllowed',
  'TargetingCountryGroupIDs',
  'TargetingCountryGroupIDsIsAllowed',
  'targetingCountriesList',
  'TargetingDeviceCountry',
  'deviceTargetingCountrySelectMode',
  'LocalTargeting',
  'Dayparting',
  'DaypartingIsAllowed',
  'TargetingCoppa',
  'TargetingLimitedAdTracking',
  'TargetingCCPA',
  'TargetingGDPR',
  'TargetingGDPRConsent',
  'Custom4',
  'Custom4Inclusion',
  'Custom5',
  'Custom5Inclusion',
  'Custom6',
  'Custom6Inclusion',
  'Custom7',
  'Custom7Inclusion',
  'Custom8',
  'Custom8Inclusion',
  'Custom9',
  'Custom9Inclusion',
  'Custom10',
  'Custom10Inclusion',
  'Custom11',
  'Custom11Inclusion',
  'Custom12',
  'Custom12Inclusion',
  'Custom13',
  'Custom13Inclusion',
  'Custom14',
  'Custom14Inclusion',
  'Custom15',
  'Custom15Inclusion',
  'Custom16',
  'Custom16Inclusion',
  'Custom17',
  'Custom17Inclusion',
  'Custom18',
  'Custom18Inclusion',
  'CustomTargeting',
  'CustomTargetingInclusion',
  'TargetingDevices',
  'TargetingDevicesIsAllowed',
  'TargetingDeviceTypes',
  'TargetingDeviceTypesIsAllowed',
  'TargetingDeviceSubTypes',
  'TargetingDeviceSubTypesIsAllowed',
  'IPFilter',
  'TargetingAppBundleID',
  'TargetingAppBundleIDIsAllowed',
  'TargetingDealID',
  'TargetingDealIDIsAllowed',
  'TargetingFWSectionIDs',
  'TargetingFWSectionIDsIsAllowed',
  'LivestreamTargeting',
  'TargetingAudienceSegmentID',
  'TargetingAudienceSegmentIDIsAllowed',
  'SegmentTargeting',
  'ContextualSegmentIDS',
  'TargetingSitePages',
  'TargetingSitePagesIsAllowed',
  'TargetingSiteDomain',
  'TargetingSiteDomainIsAllowed',
]);
